/**
 * SEO Configuration
 * Centralized SEO settings for all routes
 */

import type {OrganizationSchema, RouteConfig, SEOConfig} from '@/shared/types/seo.types.ts';

/**
 * Global SEO Configuration
 */
export const seoConfig: SEOConfig = {
  siteName: 'Tangi',
  siteDescription: 'Create personalized 3D-printed toys and figurines with AI in seconds',
  siteUrl: 'https://tangi.ai',
  defaultOGImage: 'https://tangi.ai/og-images/default.jpg',
  locale: 'en_US',
  language: 'en',
  // twitterHandle: undefined, // Add when Twitter account is created
  organizationName: 'Tangi',
  organizationLogo: 'https://tangi.ai/logo.png',
  contactEmail: 'info@tangi.ai',
};

/**
 * Organization Structured Data (for homepage)
 */
const organizationSchema: OrganizationSchema = {
  '@context': 'https://schema.org',
  '@type': 'Organization',
  name: seoConfig.organizationName,
  url: seoConfig.siteUrl,
  logo: seoConfig.organizationLogo,
  description: 'AI-generated 3D-printed personalized toys and figurines',
  contactPoint: {
    '@type': 'ContactPoint',
    email: seoConfig.contactEmail,
    contactType: 'customer service',
  },
  foundingDate: '2024',
};

/**
 * Route-specific SEO Configurations
 */
const routeConfigs: RouteConfig[] = [
  // Landing Page
  {
    path: '/',
    title: 'Create Personalized 3D-Printed Toys with AI',
    description:
      'Design unique 3D-printed toys and figurines with AI in seconds. From idea to finished product – fast, simple, and completely personalized. Start creating your dream figurine today!',
    keywords: [
      '3D-printed toys',
      'AI-generated figurines',
      'personalized toys',
      '3D printing',
      'create custom figurine',
      'custom 3D toys',
    ],
    canonical: '/',
    ogImage: 'https://tangi.ai/og-images/landing.jpg',
    ogType: 'website',
    priority: 1.0,
    changefreq: 'weekly',
    structuredData: organizationSchema,
  },

  // App Entry
  {
    path: '/app',
    title: 'Start Your Creative Journey',
    description:
      'Welcome to Tangi! Start designing your own AI-generated 3D-printed figurine. Simple, fast, and completely unique.',
    canonical: '/app',
    priority: 0.9,
    changefreq: 'monthly',
  },

  // Dashboard
  {
    path: '/dashboard',
    title: 'My Dashboard',
    description: 'Manage your orders, save favorites, and track your creations.',
    canonical: '/dashboard',
    priority: 0.7,
    changefreq: 'daily',
    noindex: true, // User-specific content
  },

  // Wizard - Category Selection
  {
    path: '/create/figurine/category',
    title: 'Choose Category - Create Your Figurine',
    description:
      'Start your creative journey! Choose a category and design your own AI-generated 3D-printed figurine in minutes.',
    keywords: ['create figurine', 'choose category', '3D toy', 'custom figurine'],
    canonical: '/create/figurine/category',
    priority: 0.9,
    changefreq: 'weekly',
  },

  // Wizard - Colors
  {
    path: '/create/figurine/colors',
    title: 'Choose Colors - Design Your Figurine',
    description:
      'Select colors for your AI-generated figurine. Create unique combinations and see results instantly.',
    canonical: '/create/figurine/colors',
    priority: 0.7,
    changefreq: 'monthly',
  },

  // Wizard - Style
  {
    path: '/create/figurine/style',
    title: 'Choose Style - Personalize Your Figurine',
    description:
      'Select the style for your 3D-printed figurine. From realistic to cartoon – create exactly what you want.',
    canonical: '/create/figurine/style',
    priority: 0.7,
    changefreq: 'monthly',
  },

  // Wizard - Personality
  {
    path: '/create/figurine/personality',
    title: 'Create Personality - Bring Your Figurine to Life',
    description:
      'Describe your figurine\'s personality and let AI create a unique character that fits perfectly.',
    canonical: '/create/figurine/personality',
    priority: 0.7,
    changefreq: 'monthly',
  },

  // Wizard - Preview
  {
    path: '/create/figurine/preview',
    title: 'Preview Your Design',
    description:
      'See your AI-generated figurine in 3D! Adjust size, choose accessories, and prepare for ordering.',
    canonical: '/create/figurine/preview',
    priority: 0.8,
    changefreq: 'monthly',
  },

  // Mascot Creator (B2B)
  {
    path: '/create/mascot',
    title: 'Create Company Mascot with AI',
    description:
      'Professional mascot design for businesses. Create your unique company mascot with AI and 3D-printing.',
    keywords: ['company mascot', 'B2B toys', 'corporate mascot', 'mascot design', 'business figurine'],
    canonical: '/create/mascot',
    priority: 0.8,
    changefreq: 'weekly',
  },

  // Checkout
  {
    path: '/checkout',
    title: 'Checkout - Complete Your Order',
    description:
      'Complete your order securely with Stripe. Get your personalized 3D-printed figurine delivered home.',
    canonical: '/checkout',
    priority: 0.8,
    changefreq: 'monthly',
    noindex: true, // Transaction page
  },

  // Studio
  {
    path: '/studio',
    title: 'Design Studio - Advanced Tools',
    description:
      'Advanced design tools to create and edit your 3D models. For experienced users.',
    canonical: '/studio',
    priority: 0.6,
    changefreq: 'monthly',
  },

  // Order Confirmation
  {
    path: '/order-confirmation',
    title: 'Thank You For Your Order!',
    description: 'Your order has been received. We\'re starting to create your unique figurine immediately.',
    canonical: '/order-confirmation',
    priority: 0.5,
    changefreq: 'monthly',
    noindex: true,
  },

  // Order Status
  {
    path: '/order-status',
    title: 'Order Status - Track Your Order',
    description: 'Track your order in real-time. See where your personalized figurine is in production.',
    canonical: '/order-status',
    priority: 0.6,
    changefreq: 'daily',
    noindex: true,
  },

  // FAQ
  {
    path: '/faq',
    title: 'Frequently Asked Questions About 3D-Printed Toys',
    description:
      'Get answers to common questions about Tangi\'s design service, 3D-printing, pricing, delivery, and how to create your own unique figurine with AI.',
    keywords: [
      'FAQ',
      '3D-printing questions',
      'Tangi FAQ',
      'toy help',
      '3D-print information',
      'custom toy questions',
    ],
    canonical: '/faq',
    ogImage: 'https://tangi.ai/og-images/faq.jpg',
    priority: 1.0,
    changefreq: 'monthly',
  },

  // About Us
  {
    path: '/about',
    title: 'About Tangi - AI-Powered 3D-Printed Design Solutions',
    description:
      'Learn more about Tangi and our mission to make personalized 3D-printed products accessible to everyone through AI technology.',
    keywords: ['about Tangi', 'our mission', 'AI and 3D-printing', 'company'],
    canonical: '/about',
    ogImage: 'https://tangi.ai/og-images/about.jpg',
    priority: 0.9,
    changefreq: 'monthly',
  },

  // Careers
  {
    path: '/careers',
    title: 'Careers at Tangi - Work With AI and 3D-Printing',
    description:
      'Join the Tangi team! See our open positions and work with cutting-edge AI and 3D-printing technology.',
    keywords: ['careers', 'Tangi jobs', 'AI jobs', '3D-printing jobs', 'tech careers'],
    canonical: '/careers',
    priority: 0.7,
    changefreq: 'weekly',
  },

  // Contact
  {
    path: '/contact',
    title: 'Contact Us - We\'re Happy to Help',
    description:
      'Have questions about Tangi or our 3D-printed products? Contact us and we\'ll respond as soon as possible.',
    canonical: '/contact',
    priority: 0.8,
    changefreq: 'monthly',
  },

  // B2B Waitlist
  {
    path: '/b2b-waitlist',
    title: 'B2B Waitlist - Business Solutions',
    description:
      'Interested in Tangi\'s business solutions? Register for our B2B waitlist and get early access.',
    keywords: ['B2B', 'business solutions', 'company mascot', 'bulk order', 'corporate toys'],
    canonical: '/b2b-waitlist',
    priority: 0.7,
    changefreq: 'monthly',
  },

  // Legal Pages
  {
    path: '/privacy-policy',
    title: 'Privacy Policy - How We Protect Your Data',
    description: 'Read our privacy policy and learn how we handle and protect your personal information.',
    canonical: '/privacy-policy',
    priority: 0.5,
    changefreq: 'yearly',
  },

  {
    path: '/terms-of-service',
    title: 'Terms of Service - Rules and Conditions',
    description:
      'Read our terms of service to understand the rules for using Tangi\'s services.',
    canonical: '/terms-of-service',
    priority: 0.5,
    changefreq: 'yearly',
  },

  {
    path: '/cookie-policy',
    title: 'Cookie Policy - Information About Cookies',
    description: 'Learn about how we use cookies on tangi.ai and how you can manage them.',
    canonical: '/cookie-policy',
    priority: 0.4,
    changefreq: 'yearly',
  },
];

/**
 * Get SEO config for a specific route
 */
export function getRouteConfig(pathname: string): RouteConfig | undefined {
  // Try exact match first
  const exactMatch = routeConfigs.find((config) => config.path === pathname);
  if (exactMatch) return exactMatch;

  // Try pattern matching for dynamic routes
  const patternMatch = routeConfigs.find((config) => {
    const pattern = config.path.replace(/:[^/]+/g, '[^/]+');
    const regex = new RegExp(`^${pattern}$`);
    return regex.test(pathname);
  });

  return patternMatch;
}

/**
 * Build full title with site name
 */
export function buildFullTitle(title?: string): string {
  if (!title) return seoConfig.siteName;
  return `${title} | ${seoConfig.siteName}`;
}

/**
 * Build canonical URL
 */
export function buildCanonicalUrl(path: string): string {
  // Handle absolute URLs
  if (path.startsWith('http')) return path;

  // Build from relative path
  const cleanPath = path.startsWith('/') ? path : `/${path}`;
  return `${seoConfig.siteUrl}${cleanPath}`;
}

/**
 * Build Open Graph image URL
 */
export function buildOGImageUrl(image?: string): string {
  if (!image) return seoConfig.defaultOGImage;
  if (image.startsWith('http')) return image;
  return `${seoConfig.siteUrl}${image}`;
}
